/**
 * BeatDetector — a pure, framework-free onset + tempo estimator.
 *
 * It knows nothing about Web Audio or the paramBus. You feed it a stream of energy
 * samples (one per analysis frame, any positive scale) with a timestamp, and it
 * answers "did a beat just start, and what's the running BPM?". The browser-side
 * AudioReactor owns the microphone and wraps this; keeping the logic here (no DOM,
 * no globals) makes it unit-testable without a real AudioContext.
 *
 * Detection model — a classic adaptive-threshold onset detector:
 *   • Keep a rolling average of recent energy (the local loudness floor).
 *   • Flag a beat when an incoming sample jumps above `avg * threshold` AND enough
 *     time has passed since the last beat (`MIN_BEAT_GAP_MS`, a ~240 BPM ceiling)
 *     so a single loud hit can't machine-gun.
 *   • `threshold` is derived from a 0..1 `sensitivity`: more sensitivity ⇒ a lower
 *     multiplier ⇒ softer onsets count. This is what the `+`/`−` keys scrub.
 *
 * BPM is the rolling average of the gaps between recent detected beats — the same
 * averaging idea as TapTempo, just fed by onsets instead of Space taps.
 */

/** No two beats closer than this (ms) — caps detection at ~240 BPM and debounces. */
export const MIN_BEAT_GAP_MS = 250;
/** How many recent inter-beat gaps to average for the BPM estimate. */
export const MAX_BEAT_GAPS = 8;
/** Rolling-average smoothing for the energy floor (0..1; higher = slower to adapt). */
export const ENERGY_SMOOTHING = 0.85;
/** BPM is only published once this many beats have been seen (avoids wild first guesses). */
export const MIN_BEATS_FOR_BPM = 3;

/** Threshold multiplier at sensitivity extremes. High sensitivity ⇒ low multiplier. */
const THRESHOLD_AT_MIN_SENS = 2.2; // sensitivity 0 — only strong onsets
const THRESHOLD_AT_MAX_SENS = 1.12; // sensitivity 1 — soft onsets count too

export interface BeatResult {
  /** True on the frame a fresh beat onset was detected. */
  readonly beat: boolean;
  /** Current BPM estimate, or 0 until MIN_BEATS_FOR_BPM onsets exist. */
  readonly bpm: number;
}

export class BeatDetector {
  private avgEnergy = 0;
  private seeded = false;
  private lastBeatMs = -Infinity;
  private readonly gaps: number[] = [];
  private beatCount = 0;
  private bpmValue = 0;
  private thresholdMult: number;

  /** @param sensitivity 0..1 (default 0.5). */
  constructor(sensitivity = 0.5) {
    this.thresholdMult = threshMult(sensitivity);
  }

  get bpm(): number {
    return this.bpmValue;
  }

  /** Update detection sensitivity (0..1); takes effect on the next sample. */
  setSensitivity(sensitivity: number): void {
    this.thresholdMult = threshMult(clamp01(sensitivity));
  }

  /** Feed one energy sample at time `nowMs` (performance.now clock). */
  push(energy: number, nowMs: number): BeatResult {
    // Seed the floor on the first sample so the very first frame can't false-trigger.
    if (!this.seeded) {
      this.avgEnergy = energy;
      this.seeded = true;
      return { beat: false, bpm: this.bpmValue };
    }

    const floor = this.avgEnergy;
    // Adapt the floor toward the new sample (slow) before deciding, so sustained
    // loudness raises the bar rather than beating continuously.
    this.avgEnergy = ENERGY_SMOOTHING * this.avgEnergy + (1 - ENERGY_SMOOTHING) * energy;

    const overThreshold = energy > floor * this.thresholdMult && energy > 0;
    const spaced = nowMs - this.lastBeatMs >= MIN_BEAT_GAP_MS;
    if (!overThreshold || !spaced) {
      return { beat: false, bpm: this.bpmValue };
    }

    // A beat. Record the gap (unless this is the first onset) and re-estimate BPM.
    if (this.lastBeatMs > -Infinity) {
      this.gaps.push(nowMs - this.lastBeatMs);
      if (this.gaps.length > MAX_BEAT_GAPS) this.gaps.shift();
    }
    this.lastBeatMs = nowMs;
    this.beatCount++;

    if (this.beatCount >= MIN_BEATS_FOR_BPM && this.gaps.length > 0) {
      const sum = this.gaps.reduce((a, b) => a + b, 0);
      this.bpmValue = 60000 / (sum / this.gaps.length);
    }
    return { beat: true, bpm: this.bpmValue };
  }
}

/** Map sensitivity 0..1 to the threshold multiplier (linear between the extremes). */
function threshMult(sensitivity: number): number {
  return THRESHOLD_AT_MIN_SENS + (THRESHOLD_AT_MAX_SENS - THRESHOLD_AT_MIN_SENS) * sensitivity;
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
